'use client';

import React from 'react';
import { Control } from 'react-hook-form';
import { FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { SelectSimpleCds, SelectSimpleOption } from '@/components/cds/select-simple-cds';

export type FormFieldSelectSimpleCdsProps = Omit<React.ComponentProps<'input'>, 'form' | 'dir'> & {
  options: SelectSimpleOption[] | string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<any, any>;
  label?: string | React.ReactNode;
  description?: string | React.ReactNode;
  errorMessage?: string | React.ReactNode;
  value?: string;
  onSelectionChange?: (value: string) => void;
};

/**
 * A simple form field selector component using React and TypeScript.
 * This component renders a select dropdown with options and integrates with a form control.
 *
 * @component
 * @param {string} className - Optional additional class name for the form item.
 * @param {string} placeholder - Placeholder text for the select input.
 * @param {string} label - Label text for the form field.
 * @param {any} control - Control object for managing form state.
 * @param {string} name - Name of the form field (required).
 * @param {string} description - Optional description text for the form field.
 * @param {string} errorMessage - Optional error message to display.
 * @param {Array<{ value: string, label: string }>} options - Array of options for the select dropdown.
 * @param {function} onSelectionChange - Callback function to handle selection changes.
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded ref to the input element.
 * @throws Will throw an error if the `name` or `control` props are not provided.
 *
 * @example
 * <FormFieldSelectSimpleCds
 *   className="custom-class"
 *   placeholder="Select an option"
 *   label="Example Label"
 *   control={control}
 *   name="exampleName"
 *   description="This is a description"
 *   errorMessage="This is an error message"
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' }
 *   ]}
 *   onSelectionChange={(value) => console.log(value)}
 * />
 */
const FormFieldSelectSimpleCds = React.forwardRef<HTMLInputElement, FormFieldSelectSimpleCdsProps>(
  (
    {
      className,
      placeholder,
      label,
      control,
      name,
      description,
      errorMessage,
      options,
      onSelectionChange,
      ...props
    },
    ref
  ) => {
    if (!name) {
      throw new Error(`FormFieldSelectSimpleCds 'name' is required (label: ${label})`);
    }

    if (!control) {
      throw new Error(`FormFieldSelectSimpleCds 'control' is required (name: ${name})`);
    }

    return (
      <FormField
        {...props}
        control={control}
        name={name}
        render={({ field }) => (
          <FormItem className={className} ref={ref}>
            {label && <FormLabel>{label}</FormLabel>}
            <SelectSimpleCds
              options={options}
              placeholder={placeholder}
              hasFormControl={true}
              defaultValue={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                onSelectionChange?.(value);
              }}
            />
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage>{errorMessage || ''}</FormMessage>
          </FormItem>
        )}
      />
    );
  }
);
FormFieldSelectSimpleCds.displayName = 'FormFieldSelectSimpleCds';

export { FormFieldSelectSimpleCds };
